import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  House, 
  EnvelopeSimple, 
  CalendarCheck, 
  UserCircle, 
  ChatCircleDots,
  Users,
  Article,
  Check,
  Clock,
  X,
  ArrowLeft,
  Eye,
  Quotes,
  Star
} from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [anonymousMessages, setAnonymousMessages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, contactsRes, bookingsRes, anonymousRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/anonymous-messages`, { withCredentials: true }),
        axios.get(`${API}/admin/testimonials`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setBookings(bookingsRes.data);
      setAnonymousMessages(anonymousRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to update booking:', error);
    }
  };

  const markMessageResponded = async (messageId) => {
    try {
      await axios.put(`${API}/admin/anonymous-messages/${messageId}/respond`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to mark message:', error);
    }
  };

  const updateTestimonialStatus = async (testimonialId, status) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonialId}/status`, { status }, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to update testimonial:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-['Outfit'] font-semibold text-[#2A2825] mb-2">Access Denied</h2>
          <p className="text-[#5C564F]">You need admin privileges to view this page.</p>
          <button onClick={onClose} className="mt-4 text-[#B46C4A] hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: House },
    { id: 'contacts', label: 'Messages', icon: EnvelopeSimple, count: stats?.contacts },
    { id: 'bookings', label: 'Bookings', icon: CalendarCheck, count: stats?.pending_bookings },
    { id: 'anonymous', label: 'Anonymous', icon: UserCircle, count: stats?.unresponded_anonymous },
    { id: 'testimonials', label: 'Testimonials', icon: Quotes, count: stats?.pending_testimonials }
  ];

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-[#E5DCD0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onClose}
              className="w-10 h-10 bg-[#F2EBE1] hover:bg-[#E5DCD0] rounded-full flex items-center justify-center transition-colors"
              data-testid="close-dashboard-btn"
            >
              <ArrowLeft size={20} className="text-[#2A2825]" />
            </button>
            <div>
              <h1 className="text-xl font-['Outfit'] font-semibold text-[#2A2825]">Admin Dashboard</h1>
              <p className="text-sm text-[#5C564F]">Welcome back, Adeseun</p>
            </div>
          </div>
          <button 
            onClick={fetchData}
            className="text-sm text-[#B46C4A] hover:text-[#9B5C3E] font-medium"
          >
            Refresh Data
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium whitespace-nowrap transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-[#B46C4A] text-white'
                  : 'bg-white text-[#5C564F] hover:bg-[#F2EBE1] border border-[#E5DCD0]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={20} weight={activeTab === tab.id ? 'fill' : 'regular'} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-[#B46C4A] text-white'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loader"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && stats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
              >
                <div className="bg-white rounded-2xl p-6 border border-[#E5DCD0]">
                  <div className="w-12 h-12 bg-[#B46C4A]/10 rounded-xl flex items-center justify-center mb-4">
                    <EnvelopeSimple size={24} className="text-[#B46C4A]" weight="fill" />
                  </div>
                  <p className="text-3xl font-['Outfit'] font-semibold text-[#2A2825]">{stats.contacts}</p>
                  <p className="text-sm text-[#5C564F]">Contact Messages</p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-[#E5DCD0]">
                  <div className="w-12 h-12 bg-[#647B64]/10 rounded-xl flex items-center justify-center mb-4">
                    <CalendarCheck size={24} className="text-[#647B64]" weight="fill" />
                  </div>
                  <p className="text-3xl font-['Outfit'] font-semibold text-[#2A2825]">{stats.bookings}</p>
                  <p className="text-sm text-[#5C564F]">Session Bookings</p>
                  {stats.pending_bookings > 0 && (
                    <p className="text-xs text-[#B46C4A] mt-1">{stats.pending_bookings} pending</p>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-[#E5DCD0]">
                  <div className="w-12 h-12 bg-[#E28B5E]/10 rounded-xl flex items-center justify-center mb-4">
                    <UserCircle size={24} className="text-[#E28B5E]" weight="fill" />
                  </div>
                  <p className="text-3xl font-['Outfit'] font-semibold text-[#2A2825]">{stats.anonymous_messages}</p>
                  <p className="text-sm text-[#5C564F]">Anonymous Messages</p>
                  {stats.unresponded_anonymous > 0 && (
                    <p className="text-xs text-[#B46C4A] mt-1">{stats.unresponded_anonymous} need response</p>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-[#E5DCD0]">
                  <div className="w-12 h-12 bg-[#2A2825]/10 rounded-xl flex items-center justify-center mb-4">
                    <Users size={24} className="text-[#2A2825]" weight="fill" />
                  </div>
                  <p className="text-3xl font-['Outfit'] font-semibold text-[#2A2825]">{stats.users}</p>
                  <p className="text-sm text-[#5C564F]">Registered Users</p>
                </div>
              </motion.div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {contacts.length === 0 ? (
                  <div className="text-center py-12 text-[#5C564F]">No contact messages yet.</div>
                ) : (
                  contacts.map((contact, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded-2xl p-6 border border-[#E5DCD0]"
                      data-testid={`contact-item-${index}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="font-medium text-[#2A2825]">{contact.name}</p>
                          <p className="text-sm text-[#5C564F]">{contact.email}</p>
                        </div>
                        <span className="text-xs text-[#5C564F]">{formatDate(contact.created_at)}</span>
                      </div>
                      <p className="text-[#5C564F] whitespace-pre-wrap">{contact.message}</p>
                      {contact.is_anonymous && (
                        <span className="inline-block mt-3 text-xs bg-[#F2EBE1] text-[#5C564F] px-3 py-1 rounded-full">
                          Sent anonymously
                        </span>
                      )}
                    </div>
                  ))
                )}
              </motion.div>
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {bookings.length === 0 ? (
                  <div className="text-center py-12 text-[#5C564F]">No bookings yet.</div>
                ) : (
                  bookings.map((booking, index) => (
                    <div 
                      key={booking.id} 
                      className="bg-white rounded-2xl p-6 border border-[#E5DCD0]"
                      data-testid={`booking-item-${index}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <p className="font-medium text-[#2A2825]">{booking.name}</p>
                          <p className="text-sm text-[#5C564F]">{booking.email}</p>
                          {booking.phone && <p className="text-sm text-[#5C564F]">{booking.phone}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-[#B46C4A]">
                            {booking.preferred_date} at {booking.preferred_time}
                          </p>
                          <span className={`inline-block mt-1 text-xs px-3 py-1 rounded-full ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                      
                      {booking.reason && (
                        <p className="text-[#5C564F] mb-4 p-3 bg-[#F9F6F0] rounded-xl text-sm">
                          <span className="font-medium">Reason:</span> {booking.reason}
                        </p>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          disabled={booking.status === 'confirmed'}
                          className="flex items-center gap-1 px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          <Check size={16} /> Confirm
                        </button>
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          disabled={booking.status === 'cancelled'}
                          className="flex items-center gap-1 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          <X size={16} /> Cancel
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </motion.div>
            )}

            {/* Anonymous Messages Tab */}
            {activeTab === 'anonymous' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {anonymousMessages.length === 0 ? (
                  <div className="text-center py-12 text-[#5C564F]">No anonymous messages yet.</div>
                ) : (
                  anonymousMessages.map((msg, index) => (
                    <div 
                      key={msg.id} 
                      className={`bg-white rounded-2xl p-6 border ${msg.responded ? 'border-[#E5DCD0]' : 'border-[#B46C4A]'}`}
                      data-testid={`anonymous-item-${index}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <span className={`text-xs px-3 py-1 rounded-full ${
                          msg.responded ? 'bg-green-100 text-green-700' : 'bg-[#B46C4A]/10 text-[#B46C4A]'
                        }`}>
                          {msg.responded ? 'Responded' : 'Needs Response'}
                        </span>
                        <span className="text-xs text-[#5C564F]">{formatDate(msg.created_at)}</span>
                      </div>
                      
                      <p className="text-[#5C564F] whitespace-pre-wrap mb-4">{msg.message}</p>
                      
                      {msg.mood && (
                        <p className="text-sm text-[#5C564F] mb-4">
                          <span className="font-medium">Mood:</span> {msg.mood}
                        </p>
                      )}

                      {!msg.responded && (
                        <button
                          onClick={() => markMessageResponded(msg.id)}
                          className="flex items-center gap-1 px-4 py-2 bg-[#B46C4A] hover:bg-[#9B5C3E] text-white rounded-full text-sm font-medium transition-colors"
                        >
                          <Check size={16} /> Mark as Responded
                        </button>
                      )}
                    </div>
                  ))
                )}
              </motion.div>
            )}

            {/* Testimonials Tab */}
            {activeTab === 'testimonials' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4"
              >
                {testimonials.length === 0 ? (
                  <div className="text-center py-12 text-[#5C564F]">No testimonials submitted yet.</div>
                ) : (
                  testimonials.map((testimonial, index) => (
                    <div 
                      key={testimonial.id} 
                      className={`bg-white rounded-2xl p-6 border ${
                        testimonial.status === 'pending' ? 'border-[#E28B5E]' : 
                        testimonial.status === 'approved' ? 'border-green-300' : 'border-[#E5DCD0]'
                      }`}
                      data-testid={`testimonial-item-${index}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-[#647B64] rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {testimonial.initials}
                          </div>
                          <div>
                            <p className="font-medium text-[#2A2825]">{testimonial.name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              testimonial.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              testimonial.status === 'approved' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {testimonial.status}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-[#5C564F]">{formatDate(testimonial.created_at)}</span>
                      </div>
                      
                      <div className="bg-[#F9F6F0] rounded-xl p-4 mb-4">
                        <p className="text-[#5C564F] italic">"{testimonial.quote}"</p>
                      </div>

                      {testimonial.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateTestimonialStatus(testimonial.id, 'approved')}
                            className="flex items-center gap-1 px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-full text-sm font-medium transition-colors"
                          >
                            <Check size={16} /> Approve
                          </button>
                          <button
                            onClick={() => updateTestimonialStatus(testimonial.id, 'rejected')}
                            className="flex items-center gap-1 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full text-sm font-medium transition-colors"
                          >
                            <X size={16} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
